import type { TrailerTypeInput } from '@chukua/common';
import { Button, Table } from 'antd';
import { Container, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { TableSkeleton } from '../../components/TableSkeleton';
import { buildTrailerTypeColumns } from './build-trailer-type-columns';
import type { TrailerType } from './reference-data.api';
import { TrailerTypeFormDrawer } from './TrailerTypeFormDrawer';

interface TrailerTypesPanelProps {
  types: TrailerType[];
  loading: boolean;
  saveError: string | null;
  saving: boolean;
  onCreate: (input: TrailerTypeInput) => Promise<boolean>;
  onUpdate: (id: string, input: TrailerTypeInput) => Promise<boolean>;
  onDeactivate: (row: TrailerType) => Promise<void>;
  onReactivate: (row: TrailerType) => Promise<void>;
  onResetSaveError: () => void;
}

/** Client-side paging (Ant's default) — the small-fixed-catalogue exception to server-side lists (CLAUDE.md → Lists and tables). */
export function TrailerTypesPanel({
  types,
  loading,
  saveError,
  saving,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  onResetSaveError,
}: TrailerTypesPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TrailerType | undefined>(undefined);

  function openCreate() {
    onResetSaveError();
    setEditing(undefined);
    setDrawerOpen(true);
  }

  function openEdit(row: TrailerType) {
    onResetSaveError();
    setEditing(row);
    setDrawerOpen(true);
  }

  const columns = buildTrailerTypeColumns({ onEdit: openEdit, onDeactivate, onReactivate });
  const newButton = (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={openCreate}>
      New trailer type
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">{newButton}</div>
      {loading && <TableSkeleton label="Loading trailer types" />}
      {!loading && types.length === 0 && (
        <EmptyState icon={Container} title="No trailer types yet" description="This catalogue ships empty — add the trailer types this deployment uses.">
          {newButton}
        </EmptyState>
      )}
      {!loading && types.length > 0 && <Table rowKey="id" dataSource={types} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: 'max-content' }} />}
      <TrailerTypeFormDrawer
        open={drawerOpen}
        editing={editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (editing ? onUpdate(editing.id, input) : onCreate(input))}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
